/* eslint-disable react-hooks/exhaustive-deps */
import { Stack, TextField, TextFieldProps, Theme, useTheme } from "@mui/material";
import Drawer from '@mui/material/Drawer';
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NumericFormat, NumericFormatProps } from "react-number-format";
import useIsMobile from "../../../hooks/ui-states/useIsMobile";
import { KeyboardLayout } from '../CurrencyInputKeyboard/KeyboardLayout';
import { OPERATORS, useCalculator } from '../CurrencyInputKeyboard/useCalculator';
import { useClickOutside } from "../../../hooks/ui-states/useClickOutside";

const getLocaleSeparators = (lang: string) => {
  if (lang === 'vi') {
    return { thousandSeparator: '.', decimalSeparator: ',' };
  }
  return { thousandSeparator: ',', decimalSeparator: '.' };
};

const formatExpressionNumber = (numStr: string, i18n: any): string => {
  const num = parseFloat(numStr);
  if (isNaN(num)) return numStr;

  // Use locale-specific formatting
  const locale = i18n.language?.startsWith('vi') ? 'vi-VN' : 'en-US';

  return new Intl.NumberFormat(locale, {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 20
  }).format(num);
};

const formatExpression = (numStr: string, i18n: any): string => {
  // Split by spaces to get parts like ['123', '+', '456']
  const parts = numStr.split(' ');

  return parts.map(part => {
    // If it's an operator, keep as-is
    if (OPERATORS.includes(part)) {
      return part;
    }
    // If it's a number, format it
    const num = parseFloat(part);
    if (!isNaN(num) && part !== '') {
      return formatExpressionNumber(part, i18n);
    }
    return part;
  }).join(' ');
};

// Update props to accept React Hook Form controller
type CurrencyFieldProps = Omit<TextFieldProps, "variant"> & {
  control?: Control<any>;
  name: string;
  initialValue?: string | number;
  color?: string;
  variant?: "standard" | "outlined";
  allowDecimals?: boolean;
  showKeyboard?: boolean;
  keyboardInline?: boolean;
  onConfirm?: (value: string) => void;
  fontSize?: string;
  selectOnFocus?: boolean; // When true, typing replaces existing value instead of appending
  highlightOnFocus?: boolean; // When true, shows background highlight when keyboard is open
  autoFocus?: boolean; // When true, automatically opens the keyboard when triggered
  autoFocusTrigger?: boolean; // External trigger to open keyboard (e.g., when dialog opens)
};

// Updated interface for better type safety and ref forwarding
interface CustomNumericFormatProps extends Omit<NumericFormatProps, 'onChange'> {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

// Use forwardRef to properly handle refs in the custom input component
const NumberFormatCustom = forwardRef<HTMLInputElement, CustomNumericFormatProps>(
  function NumberFormatCustom(props, ref) {
    const { i18n } = useTranslation();
    const { onChange, name, value, ...other } = props;
    const { thousandSeparator, decimalSeparator } = getLocaleSeparators(i18n.language);

    const isExpression = useMemo(() => {
      const valueStr = value?.toString() || '';
      const isOnlyMinus = valueStr.trim() === '-';
      const isNumericString = !Number.isNaN(Number(valueStr));
      const containsAnyOperator = OPERATORS.some(operator => valueStr.includes(operator));
      // Only treat as expression if it contains operators AND is NOT a valid number (including negative)
      return !isOnlyMinus && !isNumericString && containsAnyOperator;
    }, [value]);

    const formattedExpression = useMemo(() => {
      if (!isExpression) return value?.toString() || '';
      return formatExpression(value?.toString() || '', i18n);
    }, [value, isExpression, i18n.language]);

    // For expressions, use a simple input
    if (isExpression) {
      // Filter out NumericFormat-specific props that don't belong on a basic input
      const {
        decimalSeparator: _ds, thousandSeparator: _ts, allowLeadingZeros: _alz,
        decimalScale: _dsc, fixedDecimalScale: _fds, customInput: _ci,
        onValueChange: _ovc, isAllowed: _ia, renderText: _rt, getInputRef: _gir,
        displayType: _dt, prefix: _p, suffix: _s, defaultValue: _dv,
        ...validInputProps
      } = other;

      return (
        <input
          {...validInputProps}
          ref={ref}
          name={name}
          value={formattedExpression}
          onChange={(e) => {
            // When user types, we need to clean the formatted value back to raw expression
            const rawValue = e.target.value.replace(/[.,]/g, (match, offset, string) => {
              // Determine if this is a thousand separator or decimal separator
              const afterMatch = string.substring(offset + 1);

              // If it's followed by exactly 3 digits and then space/operator/end, it's likely a thousand separator
              if (i18n.language?.startsWith('vi')) {
                // Vietnamese: thousand = '.', decimal = ','
                if (match === '.' && /^\d{3}(\s|[+\-×:]|$)/.test(afterMatch)) {
                  return ''; // Remove thousand separator
                }
                return match; // Keep decimal separator
              } else {
                // English: thousand = ',', decimal = '.'
                if (match === ',' && /^\d{3}(\s|[+\-×:]|$)/.test(afterMatch)) {
                  return ''; // Remove thousand separator
                }
                return match; // Keep decimal separator
              }
            });

            onChange({
              target: {
                name: name,
                value: rawValue,
              },
            });
          }}
          type="text"
          autoComplete="off"
        />
      );
    }

    // For single numbers, use NumericFormat
    return (
      <NumericFormat
        {...other}
        value={value}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: name,
              value: values.value, // Use values.value directly to preserve decimal precision
            },
          });
        }}
        thousandSeparator={thousandSeparator}
        decimalSeparator={decimalSeparator}
        allowLeadingZeros={false}
        allowNegative={true}
        decimalScale={20}
        fixedDecimalScale={false}
        inputMode="none"
        type="text"
        autoComplete="off"
        readOnly={other.readOnly}
      />
    );
  }
);

// Ensure the display name is set for dev tools
NumberFormatCustom.displayName = 'NumberFormatCustom';

const CurrencyField = forwardRef<HTMLDivElement, CurrencyFieldProps>(
  ({ control, name, initialValue, variant = "standard", color, placeholder = '0', allowDecimals = true, showKeyboard = true, keyboardInline = false, onConfirm, onChange, selectOnFocus = false, highlightOnFocus = false, autoFocus = false, autoFocusTrigger, ...props }, ref) => {
    const theme: Theme = useTheme();
    const isMobile = useIsMobile();
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
    const [hasStartedTyping, setHasStartedTyping] = useState(false);
    const isClosingRef = useRef(false); // Prevent race conditions
    const isKeyboardVisibleRef = useRef(false); // For stable reference in callbacks
    const closingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Keep ref in sync with state
    useEffect(() => {
      isKeyboardVisibleRef.current = isKeyboardVisible;
    }, [isKeyboardVisible]);

    // Clean up timers on unmount
    useEffect(() => {
      return () => {
        if (closingTimerRef.current) clearTimeout(closingTimerRef.current);
      };
    }, []);

    // Handle autoFocus with external trigger - open keyboard when trigger changes to true
    useEffect(() => {
      if (autoFocus && autoFocusTrigger && isMobile && showKeyboard && !isClosingRef.current) {
        // Delay to ensure dialog/drawer animation completes
        const timer = setTimeout(() => {
          setIsKeyboardVisible(true);
          setHasStartedTyping(false);
        }, 300);
        return () => clearTimeout(timer);
      }
    }, [autoFocus, autoFocusTrigger, isMobile, showKeyboard]);

    const calculator = useCalculator(
      props.value?.toString() || initialValue?.toString() || '', () => setHasStartedTyping(true), selectOnFocus
    );

    const handleDone = (onFieldChange?: (value: string) => void): void => {
      // Prevent multiple rapid calls
      if (isClosingRef.current) return;
      isClosingRef.current = true;

      const finalValue = calculator.calculateResult(); // Get result before updating state
      calculator.handleEquals();
      calculator.clearSelection(); // Clear visual selection state
      setIsKeyboardVisible(false);

      // Clear native text selection and blur active input
      window.getSelection()?.removeAllRanges();
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      if (onFieldChange) {
        onFieldChange(finalValue);
      }

      if (onConfirm) {
        onConfirm(finalValue);
      }

      if (onChange) {
        onChange({ target: { value: finalValue } } as any);
      }

      // Reset closing flag after a short delay to allow state to settle
      if (closingTimerRef.current) clearTimeout(closingTimerRef.current);
      closingTimerRef.current = setTimeout(() => {
        isClosingRef.current = false;
      }, 100);
    };

    const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
      if (isMobile && showKeyboard) {
        // Prevent opening keyboard if we're in the middle of closing
        if (isClosingRef.current) {
          e.target.blur();
          return;
        }

        // Prevent default keyboard by making input readonly
        e.target.setAttribute('readonly', 'true');
        e.target.setAttribute('inputmode', 'none');

        // Select all text if selectOnFocus is enabled (native selection)
        if (selectOnFocus) {
          // Use setTimeout to ensure DOM value is synced before selecting
          const input = e.target;
          setTimeout(() => {
            input.select();
          }, 0);
        } else {
          e.target.blur();
        }

        setHasStartedTyping(false);
        setIsKeyboardVisible(true);
        calculator.markForReplacement(); // Enable "select all" behavior if selectOnFocus is true
      }
    };

    // Create a version that accepts fieldValue for React Hook Form integration
    const handleInputFocusWithFieldValue = (fieldValue: string) => (e: React.FocusEvent<HTMLInputElement>): void => {
      if (isMobile && showKeyboard) {
        // Prevent opening keyboard if we're in the middle of closing
        if (isClosingRef.current) {
          e.target.blur();
          return;
        }

        // Prevent default keyboard by making input readonly
        e.target.setAttribute('readonly', 'true');

        // Select all text if selectOnFocus is enabled
        if (selectOnFocus) {
          const input = e.target;
          setTimeout(() => {
            input.select();
          }, 0);
        } else {
          e.target.blur();
        }

        setHasStartedTyping(false);
        setIsKeyboardVisible(true);
        calculator.markForReplacement(); // Enable "select all" behavior if selectOnFocus is true
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, onFieldChange?: (value: string) => void): void => {
      if (!isMobile || !showKeyboard) {
        // Allow numbers, decimal point, minus sign, and basic operators
        const regex = allowDecimals ? /[^0-9.\-+×:*/x\s]/gi : /[^0-9\-+×:*/x\s]/gi;
        let newValue = e.target.value.replace(regex, '');

        // Replace common operator symbols with our internal ones
        newValue = newValue
          .replace(/\*/g, '×')  // asterisk to multiplication
          .replace(/\//g, ':')  // forward slash to division
          .replace(/x/gi, '×'); // x to multiplication (case insensitive)

        // Store the full expression in calculator inputValue to display it
        calculator.setInputValue(newValue);

        if (onFieldChange) {
          onFieldChange(newValue);
        }
      }
    };

    const KeyboardWrapper = ({ onFieldChange }: { onFieldChange?: (value: string) => void }) => {
      const keyboardRef = useClickOutside<HTMLDivElement>(() => {
        // Use ref to get latest state value (avoids stale closure)
        if (isKeyboardVisibleRef.current) {
          handleDone(onFieldChange);
        }
      });

      if (keyboardInline) {
        if (!isKeyboardVisible) return <></>;
        return (
          <div ref={keyboardRef} style={{ width: '100%' }}>
            <KeyboardLayout
              currentValue={calculator.inputValue}
              onNumberPress={calculator.handleNumberPress}
              onDecimal={calculator.handleDecimal}
              onOperationPress={calculator.handleOperationPress}
              onBackspace={calculator.handleBackspace}
              onClear={() => {
                calculator.handleClear();
                if (onFieldChange) {
                  onFieldChange('');
                }
              }}
              onTripleZero={calculator.handleTripleZero}
              onEquals={() => {
                calculator.handleEquals();
                if (onFieldChange) {
                  onFieldChange(calculator.calculateResult());
                }
              }}
              onDone={() => handleDone(onFieldChange)}
            />
          </div>
        );
      }

      return (
        <Drawer
          anchor="bottom"
          open={isKeyboardVisible}
          hideBackdrop={true}
          onClose={() => handleDone(onFieldChange)}
          sx={{
            zIndex: isKeyboardVisible ? theme.zIndex.modal + 20 : -theme.zIndex.drawer,
            pointerEvents: 'none',
          }}
          PaperProps={{
            sx: {
              pointerEvents: 'none',
            },
          }}
        >
          <div ref={keyboardRef} style={{ pointerEvents: 'auto', width: '100%' }}>
            <KeyboardLayout
              currentValue={calculator.inputValue}
              onNumberPress={calculator.handleNumberPress}
              onDecimal={calculator.handleDecimal}
              onOperationPress={calculator.handleOperationPress}
              onBackspace={calculator.handleBackspace}
              onClear={() => {
                calculator.handleClear();
                if (onFieldChange) {
                  onFieldChange('');
                }
              }}
              onTripleZero={calculator.handleTripleZero}
              onEquals={() => {
                calculator.handleEquals();
                if (onFieldChange) {
                  onFieldChange(calculator.calculateResult());
                }
              }}
              onDone={() => handleDone(onFieldChange)}
            />
          </div>
        </Drawer>
      );
    };

    const renderKeyboard = (onFieldChange?: (value: string) => void) => {
      return <KeyboardWrapper onFieldChange={onFieldChange} />;
    };

    // If control is provided, use Controller, otherwise render directly
    if (control) {
      return (
        <Stack alignItems="center" justifyContent="center" spacing={2}>
          {variant === "outlined" ? (
            <Controller
              control={control}
              name={name}
              render={({ field: { onChange: onFieldChange, value: fieldValue, ...field }, fieldState: { error } }) => {
                const { slotProps, ...rest } = props;
                return (
                  <TextField
                    {...rest}
                    {...field}
                    fullWidth
                    ref={ref}
                    inputRef={props.inputRef}
                    placeholder={placeholder}
                    type="text"
                    variant="outlined"
                    value={isKeyboardVisible && hasStartedTyping ? calculator.inputValue : (fieldValue || '')}
                    onFocus={handleInputFocusWithFieldValue(fieldValue)}
                    onChange={(e) => handleInputChange(e as React.ChangeEvent<HTMLInputElement>, onFieldChange)}
                    error={!!error}
                    helperText={error?.message}
                    slotProps={{
                      ...slotProps,
                      input: {
                        ...slotProps?.input,
                        inputComponent: NumberFormatCustom as any,
                      },
                    }}
                  />
                );
              }}
            />
          ) : (
            <Controller
              control={control}
              name={name}
              render={({ field: { onChange: onFieldChange, value: fieldValue, ...field }, fieldState: { error } }) => {
                return (
                  <TextField
                    {...props}
                    {...field}
                    ref={ref}
                    inputRef={props.inputRef}
                    fullWidth
                    type="text"
                    variant="standard"
                    placeholder={placeholder}
                    value={isKeyboardVisible && hasStartedTyping ? calculator.inputValue : (fieldValue || '')}
                    onFocus={handleInputFocusWithFieldValue(fieldValue)}
                    onChange={(e) => handleInputChange(e as React.ChangeEvent<HTMLInputElement>, onFieldChange)}
                    error={!!error}
                    helperText={error?.message}
                    slotProps={{
                      input: {
                        inputComponent: NumberFormatCustom as any,
                        sx: {
                          fontSize: props.fontSize || 40,
                          textAlign: "center",
                          fontWeight: theme.typography.fontWeightBold,
                          color: color || theme.palette.text.primary,
                          "&:hover:not(.Mui-disabled, .Mui-error):before, &:hover:not(.Mui-disabled, .Mui-error):after, &::before, &::after":
                          {
                            borderBottomColor: color || theme.palette.text.primary,
                          },
                          "& input": {
                            textAlign: "center",
                          },
                        },
                      },
                    }}
                  />
                );
              }}
            />
          )}

          {isMobile && showKeyboard && (
            <Controller
              control={control}
              name={name}
              render={({ field: { onChange: onFieldChange } }) => renderKeyboard(onFieldChange)}
            />
          )}
        </Stack>
      );
    }

    // When used outside of React Hook Form
    // Merge slotProps from props with selection styling
    const { slotProps: propsSlotProps, ...restProps } = props;

    return (
      <Stack alignItems="center" justifyContent="center" spacing={2}>
        <TextField
          {...restProps}
          ref={ref}
          inputRef={props.inputRef}
          type="text"
          name={name}
          variant="standard"
          placeholder={placeholder}
          value={calculator.inputValue}
          onFocus={handleInputFocus}
          onChange={handleInputChange}
          slotProps={{
            input: {
              ...propsSlotProps?.input,
              inputComponent: NumberFormatCustom as any,
              sx: {
                fontSize: props.fontSize || 40,
                marginBottom: 1,
                textAlign: "center",
                fontWeight: theme.typography.fontWeightBold,
                color: color || theme.palette.text.primary,
                "&:hover:not(.Mui-disabled, .Mui-error):before, &:hover:not(.Mui-disabled, .Mui-error):after, &::before, &::after":
                {
                  borderBottomColor: color || theme.palette.text.primary,
                },
                "& input": {
                  textAlign: "center",
                },
                ...(propsSlotProps?.input as any)?.sx,
                // Apply highlight AFTER props to ensure it takes precedence
                ...(highlightOnFocus && isKeyboardVisible && {
                  "& input": {
                    ...((propsSlotProps?.input as any)?.sx?.['& input']),
                    color: theme.palette.primary.main,
                    textDecorationLine: 'underline',
                    textDecorationColor: theme.palette.primary.main,
                    textDecorationThickness: '2px',
                    textUnderlineOffset: '2px',
                  },
                }),
              },
            },
          }}
        />
        {isMobile && showKeyboard && renderKeyboard()}
      </Stack>
    );
  }
);

CurrencyField.displayName = "CurrencyField";

export default CurrencyField;
